import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil, switchMap } from 'rxjs';

// primeNg
import { PasswordModule } from 'primeng/password';

// services
import { AuthService } from '@ui-nuvertos/auth';
import { ToasterService } from '@ui-nuvertos/auth';
import { BrandingService, TenantBranding } from '../services/branding.service';
import { TenantModuleService } from '../modules/tenant-module.service';

// utils
import { getTenantFromHostname } from '../utils/tenant.util';

@Component({
  selector: 'login',
  imports: [CommonModule, ReactiveFormsModule, PasswordModule],
  standalone: true,
  templateUrl: './login.html',
  styleUrl: './login.scss',
})

export class Login implements OnInit, OnDestroy {
  loginForm!: FormGroup;
  loading = false;
  brandingLoading = true;
  
  // Branding properties
  tenantName: string = '';
  logoUrl: string = '';
  tagLine: string = 'Welcome back! Please sign in to continue.';
  
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private toasterService: ToasterService,
    private router: Router,
    private brandingService: BrandingService,
    private tenantModuleService: TenantModuleService
  ) {

    this.loginForm = this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.maxLength(64)
        ]
      ],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  ngOnInit(): void {
    // Clear any redirect flag that might have been set during 401 redirect
    sessionStorage.removeItem('auth_redirecting');
    
    // Get initial tenant name from hostname (fallback)
    try {
      const rawTenant = getTenantFromHostname();
      this.tenantName = rawTenant.charAt(0).toUpperCase() + rawTenant.slice(1);
    } catch (error) {
      console.error('Error extracting tenant name:', error);
      this.tenantName = '';
    }
    
    // Fetch branding from API
    this.brandingService.getBranding()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (branding: TenantBranding) => {
          this.brandingLoading = false;
          if (branding.display_name) {
            this.tenantName = branding.display_name;
          }
          if (branding.logo_url) {
            this.logoUrl = branding.logo_url;
          }
          if (branding.tag_line) {
            this.tagLine = branding.tag_line;
          }
        },
        error: (err) => {
          console.error('Error fetching branding:', err);
          this.brandingLoading = false;
          // Keep the fallback tenant name from hostname
        }
      });
    
    this.authService.loadUserSession()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          if (user) {
            this.router.navigate(['/']);
          }
        },
        error: (err) => {
          console.error('Error loading user session:', err);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get email() {
    return this.loginForm.get('email');
  }

  get password() {
    return this.loginForm.get('password');
  }
  // Method to call login API and validate
  onLogin(): void {
    if (this.loginForm.invalid) {
      this.toasterService.error('Login failed', 'Invalid credentials');
      return;
    }

    this.loading = true;
    const { email, password } = this.loginForm.value;
    this.authService.login(email, password)
      .pipe(
        switchMap(() => this.authService.loadUserSession()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (user) => {
          this.loading = false;
          this.toasterService.success('Login successful', 'Welcome!');
          // Seed tenant modules into IndexedDB and arm polling, then navigate.
          // Awaiting the seed BEFORE navigation matters — otherwise the layout
          // mounts before the modules signal is populated and the sidebar
          // renders empty (the computed runs once with [], component then
          // doesn't always re-paint on the later signal write).
          if (user) {
            this.tenantModuleService.loadAfterLogin().finally(() => {
              this.router.navigate(['/settings']);
            });
          } else {
            this.router.navigate(['/settings']);
          }
        },
        error: (err) => {
          this.loading = false;
          // Provide more specific error messages
          const errorMessage = err?.error?.error || err?.message || 'Invalid email or password';
          if (err?.status === 401 || err?.status === 403) {
            this.toasterService.error('Authentication Failed', errorMessage);
          } else if (err?.status === 0 || err?.status >= 500) {
            this.toasterService.error('Server Error', 'Unable to connect to server. Please try again.');
          } else {
            this.toasterService.error('Login Failed', errorMessage);
          }
        }
      });
  }

}
